import React, { useContext, useState, useEffect, useCallback } from "react";
import { Container, Grid, Box, Button } from "@mui/material";
import { EventNote as EventNoteIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import CcpSchoolBreadCrumbs from "../../Layout/CcpSchoolBreadCrumbs";
import RostersInProgress from "./inc/RostersInProgress";
import AdditionalReport from "./inc/AdditionalReport";
import CoursePlanning from "./inc/CoursePlanning";
import NewCourseRequest from "./inc/NewCourseRequest";
import ChangeCourseRequest from "./inc/ChangeCourseRequest";
import StudentDropForm from "./inc/StudentDropForm";
import CourseProctorForm from "./inc/CourseProctorApp";
import { SchoolContext } from "../../../config/SchoolContext";
import { useAuth } from "../../../config/AuthContext";
import { faChartArea } from "@fortawesome/free-solid-svg-icons";
import { useTheme } from "@mui/material/styles";
import GetCoordIds from "../../CcpConstants/GetCoordIds";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../../config/firebase";
import {
  isSpecificFormVisible,
  isSchoolAuthorizedForForm,
} from "../../../utils/formVisibility";
import useCoursePlanningBetaAccess from "../../../hooks/useCoursePlanningBetaAccess";

const CcpSchoolRestrictedCourses = () => {
  const { schoolId } = useContext(SchoolContext);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const pageName = "Restricted Courses";
  const icon = faChartArea;
  const theme = useTheme();
  const linkColor = theme.palette.mode === "dark" ? "white" : "black";
  const formId = "ix25Z19hd05vXVjPivyG"; // Document ID in Firestore
  const [formData, setFormData] = useState({
    dropForm: "",
    proctorForm: "",
    testForm: "",
    courseChange: "",
    newCourseRequest: "",
    coursePlanning: "",
  });
  const [loadingForms, setLoadingForms] = useState(true);
  const [formsError, setFormsError] = useState(null);

  const {
    pathwayEmail,
    opEmail,
    loading: emailsLoading,
    error: emailsError,
  } = GetCoordIds(schoolId);

  const {
    enabled: isCoursePlanningBetaEnabled,
    loading: coursePlanningBetaLoading,
  } = useCoursePlanningBetaAccess();

  const loadFormData = useCallback(async () => {
    try {
      const docRef = doc(db, "forms", formId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setFormData(docSnap.data());
      } else {
        setFormsError(new Error("No such document!"));
      }
    } catch (error) {
      setFormsError(error);
    } finally {
      setLoadingForms(false);
    }
  }, [formId]);

  useEffect(() => {
    loadFormData();
  }, [loadFormData]);

  const handleCoursePlanningOpen = () => {
    navigate("/CcpSchoolCoursePlanning");
  };

  if (emailsLoading || loadingForms || coursePlanningBetaLoading) {
    return <div>Loading...</div>; // Or a more suitable loading indicator
  }

  if (emailsError || formsError) {
    return <div>Error: {emailsError?.message || formsError?.message}</div>;
  }

  return (
    <Container sx={{ marginTop: "100px" }}>
      <Box sx={{ flexGrow: 1 }}>
        <CcpSchoolBreadCrumbs pageName={pageName} icon={icon} />
        <Grid container spacing={2}>
          {/* Course Planning Button — visibility now driven by CcpBetaTesting
              (managed on the admin "Beta Testing" page), not a hardcoded list */}
          {isCoursePlanningBetaEnabled && (
            <Grid item xs={3} sx={{ marginTop: "20px" }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<EventNoteIcon />}
                onClick={handleCoursePlanningOpen}
                sx={{ color: "white" }}
              >
                Course Planning
              </Button>
            </Grid>
          )}
          {/* New Course Request */}
          {isSpecificFormVisible(
            formData,
            "newCourseRequest",
            currentUser?.school_name,
          ) && (
            <Grid item xs={3} sx={{ marginTop: "20px" }}>
              <NewCourseRequest
                schoolId={schoolId}
                pathwayEmail={pathwayEmail}
                opEmail={opEmail}
              />
            </Grid>
          )}
          {isSpecificFormVisible(
            formData,
            "courseChange",
            currentUser?.school_name,
          ) && (
            <Grid item xs={3} sx={{ marginTop: "20px" }}>
              <ChangeCourseRequest
                schoolId={schoolId}
                pathwayEmail={pathwayEmail}
                opEmail={opEmail}
              />
            </Grid>
          )}
          {isSpecificFormVisible(
            formData,
            "dropForm",
            currentUser?.school_name,
          ) && (
            <Grid item xs={3} sx={{ marginTop: "20px" }}>
              <StudentDropForm
                schoolId={schoolId}
                pathwayEmail={pathwayEmail}
                opEmail={opEmail}
              />
            </Grid>
          )}
          {isSpecificFormVisible(
            formData,
            "proctorForm",
            currentUser?.school_name,
          ) && (
            <Grid item xs={3} sx={{ marginTop: "20px" }}>
              <CourseProctorForm
                schoolId={schoolId}
                pathwayEmail={pathwayEmail}
                opEmail={opEmail}
              />
            </Grid>
          )}
        </Grid>

        <Box sx={{ flexGrow: 1, marginTop: "20px" }}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <RostersInProgress schoolId={schoolId} linkColor={linkColor} />
            </Grid>
            <Grid item xs={4}>
              <AdditionalReport schoolId={schoolId} linkColor={linkColor} />
            </Grid>
            <Grid item xs={4}>
              <CoursePlanning schoolId={schoolId} linkColor={linkColor} />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Container>
  );
};

export default CcpSchoolRestrictedCourses;
